import { useEffect } from "react";
import "./App.css";
import { Route, Routes } from "react-router-dom";
import Login from "./pages/public/Login";
import ForgotPassword from "./pages/public/ForgotPassword";
import Signup from "./pages/public/Signup";
import VerifyOTP from "./pages/public/VerifyOTP";
import ProtectedLayout from "./components/ProtectedLayout";
import PublicLayout from "./components/public/PublicLayout";
import SearchSessions from "./pages/protected/SearchSessions";
import Nyaynet404 from "./pages/public/Nyaynet404";
import AdvocateObjectionPanel from "./pages/protected/advocate/AdvocateObjectionPanel";
import UserDashboard from "./pages/protected/UserDashboard";
import AdvocateDashboard from "./pages/protected/advocate/AdvocateDashboard";
import JudgeDashboard from "./pages/protected/judge/JudgeDashboard";
import NyayNetUsers from "./pages/protected/NyayNetUsers";
import StopReasonsManager from "./pages/protected/StopReasonsManager";
import CauseList from "./pages/protected/CauseList";
import { useAuth } from "./context/AuthProvider";
import SetPassword from "./pages/public/SetPassword";
import SuperAdminDashboard from "./pages/protected/SuperAdminDashboard";
import ArchivalActivity from "./pages/protected/reports/ArchivalActivity";
import DailyListReport from "./pages/protected/reports/DailyListReport";
import LoginActivity from "./pages/protected/reports/LoginActivity";
import RegistrationReport from "./pages/protected/reports/RegistrationReport";
import StreamingActivity from "./pages/protected/reports/StreamingActivity";
import StreamViewers from "./pages/protected/reports/StreamViewers";
import Streams from "./pages/protected/reports/Streams";
import AdvocateLobby from "./pages/protected/advocate/AdvocateLobby";
import ObjectionRequestsController from "./pages/protected/advocate/ObjectionRequestPage";
import VideoPlayer from "./pages/protected/VideoPlayer";
import ArchivalReport from "./pages/protected/reports/ArchivalReport";
import ProfileContainer from "./pages/protected/profile/ProfileContainer";
import FullPageLoader from "./components/FullPageLoader";
import ArchivedVideoPage from "./pages/protected/reports/ArchivedVideoPage";
import StreamViewRecording from "./pages/protected/reports/StreamViewRecording";
import ReaderDashboard from "./pages/protected/reader/ReaderDashboard";
import UserArchival from "./pages/protected/archival/UserArchival";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import FlowProtectedRoute from "./components/FlowProtectedRoute";
import { LiveStreamPlayer } from "./pages/LiveStreamPlayer";
import WatchVideo from "./pages/protected/WatchVideo";
import RetentionSettings from "./pages/protected/retention-duration/index";
import ControllerBench from "./pages/protected/controller/ControllerBench";
import CourtroomPlayer from "./components/mediasoup-ui/CourtRoomPlayer";
import NewCourts from "./pages/protected/new-courts/NewCourts";
import ControllerDashboard from "./pages/protected/controller/main-dahsboard/ControllerDashboard";
import ScrollToTop from "./utils/ScrollToTop";
import Streaming from "./pages/public/Streaming";
import Recording from "./pages/public/Recording";
import PublicAuthLayout from "./components/public/PublicAuthLayout";
import RecordingPlayer from "./pages/public/RecordingPlayer";
import JoinMeeting from "./pages/public/JoinMeeting";
import PublicViewerPage from "./components/public-user/PublicViewerPage";
import PublicMeetingLayout from "./components/public/PublicMeetingLayout";
import HostPreJoin from "./pages/protected/HostPreJoin";
import PublicMeeting from "./pages/protected/public-meeting/Public.Meeting";
import Meetings from "./pages/public/Meetings";

function App() {
  const { user } = useAuth();
  const getDashboard = () => {
    switch (user?.role) {
      case "SuperAdmin":
        return <SuperAdminDashboard />;
      case "Judge":
        return <JudgeDashboard />;
      case "Advocate":
        return <AdvocateDashboard />;
      case "Reader":
        return <ReaderDashboard />;
      case "Controller":
        return <ControllerDashboard />;
      default:
        return <UserDashboard />;
    }
  };

  useEffect(() => {
    const handleWheel = (e: any) => {
      if (e.target.type === "number") {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <>
      <FullPageLoader />
      <ScrollToTop />
      <Routes>
        <Route element={<PublicMeetingLayout />}>
          <Route path="/join-session" element={<JoinMeeting />} />
        </Route>
        <Route path="/meeting" element={<PublicMeeting />} />
        <Route element={<PublicLayout />}>
          {/* <Route path="/" element={<HomePage />} /> */}
          <Route element={<PublicAuthLayout />}>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/signup" element={<Signup />} />
            <Route element={<FlowProtectedRoute />}>
              <Route path="/otp-verification" element={<VerifyOTP />} />
              <Route path="/set-password" element={<SetPassword />} />
            </Route>
          </Route>
          <Route path="/streaming" element={<Streaming />} />
          <Route path="/recordings" element={<Recording />} />
          <Route path="/watch" element={<RecordingPlayer />} />
          <Route path="/vc-links" element={<Meetings />} />
        </Route>
        <Route element={<ProtectedLayout />}>
          {/* Dashboard (all roles) */}
          <Route path="/dashboard" element={getDashboard()} />
          <Route path="/meeting" element={<PublicMeeting />} />
          <Route path="/host-prejoin" element={<HostPreJoin />} />
          <Route path="/meetings" element={<Meetings />} />
          {/* ---------- SUPER ADMIN ---------- */}
          <Route element={<RoleProtectedRoute allowedRoles={["SuperAdmin"]} />}>
            <Route path="/internal-users" element={<NyayNetUsers />} />

            <Route path="/courts" element={<NewCourts />} />

            <Route path="/stop-reasons" element={<StopReasonsManager />} />

            <Route path="/reports/archival" element={<ArchivalActivity />} />

            <Route
              path="/reports/archival/video"
              element={<ArchivedVideoPage />}
            />

            <Route
              path="/reports/archival-report"
              element={<ArchivalReport />}
            />

            <Route path="/reports/streaming" element={<StreamingActivity />} />

            <Route path="/reports/daily-list" element={<DailyListReport />} />

            <Route path="/reports/login" element={<LoginActivity />} />
            <Route
              path="/reports/registration"
              element={<RegistrationReport />}
            />

            <Route path="/reports/stream-viewer" element={<StreamViewers />} />

            <Route path="/reports/streams" element={<Streams />} />
          </Route>
          {/* ---------- ADVOCATE ---------- */}
          <Route element={<RoleProtectedRoute allowedRoles={["Advocate"]} />}>
            <Route
              path="/objection-form"
              element={<AdvocateObjectionPanel />}
            />
            <Route
              path="/objection-list"
              element={<ObjectionRequestsController />}
            />
            <Route path="/advocate-lobby" element={<AdvocateLobby />} />
          </Route>
          {/* ---------- COMMON (ALL LOGGED USERS) ---------- */}
          <Route
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  "SuperAdmin",
                  "Judge",
                  "Advocate",
                  "Controller",
                  "Reader",
                  "Individual",
                ]}
              />
            }
          >
            <Route
              path="/watch-live/:courtRoomNo"
              element={<CourtroomPlayer />}
            />

            <Route path="/search-session" element={<SearchSessions />} />
            <Route path="/stream" element={<VideoPlayer />} />
            <Route path="/archival" element={<UserArchival />} />
            <Route path="/profile" element={<ProfileContainer />} />
            <Route path="/recording" element={<StreamViewRecording />} />
            <Route path="/archival-history" element={<ArchivalActivity />} />
            <Route path="/live-stream" element={<LiveStreamPlayer />} />
            <Route path="/watch-video" element={<WatchVideo />} />
          </Route>

          {/* ---------- SUPER ADMIN + CONTROLLER ---------- */}
          <Route
            element={
              <RoleProtectedRoute allowedRoles={["SuperAdmin", "Controller"]} />
            }
          >
            <Route path="/causelist" element={<CauseList />} />

            <Route path="/archival-requests" element={<ArchivalActivity />} />

            <Route path="/retention-duration" element={<RetentionSettings />} />

            <Route
              path="/objection-requests"
              element={<ObjectionRequestsController />}
            />
          </Route>

          {/* ---------- CONTROLLER ONLY ---------- */}

          <Route element={<RoleProtectedRoute allowedRoles={["Controller"]} />}>
            <Route path="/bench-management" element={<ControllerBench />} />
          </Route>
        </Route>
        <Route
          path="/public-viewer/:courtRoomNo"
          element={<PublicViewerPage />}
        />

        <Route path="*" element={<Nyaynet404 />} />
      </Routes>
    </>
  );
}
export default App;
